/**
 * Who can sign in, and what they may do once they have.
 *
 * A NON-ADMIN SEES THE TABLE AND NO CONTROLS AT ALL — not greyed-out ones. The
 * contract lets any role read `GET /api/users`, so hiding the list would be
 * hiding something the server will hand over anyway; but a disabled button is a
 * promise that pressing it is a matter of timing, and for a viewer looking at a
 * Delete button it never will be. One sentence saying user management is
 * admin-only carries the same information without the false affordance. This is
 * the same rule the agent form follows for a viewer.
 *
 * THE SIGNED-IN PERSON'S OWN ROW OFFERS NO DEMOTE, NO DISABLE AND NO DELETE. The
 * server refuses all three with `self_target`, so every one of them would be a
 * button whose only outcome is an error — and the mistake it protects against is
 * the one that cannot be undone from this screen, because after it there is
 * nobody with the rights to undo it. `last_admin` is the same shape of accident
 * one step further out, and both messages are surfaced in full below in case a
 * second admin's stale tab reaches the server first.
 *
 * EVERY WRITE IS FOLLOWED BY A RE-READ OF THE LIST, and nothing reads what the
 * write returned. The contract fixes response shapes for the auth routes and for
 * none of these, so a PATCH answering 204 and a PATCH answering with the row are
 * both correct — see lib/auth.ts. The GET costs one round trip on an action that
 * happens a few times a week and is the only version of this table that is
 * certainly the server's, which on the screen that decides who has access is
 * worth more than the round trip.
 *
 * THE ROWS ARE SORTED HERE, WHICH DATA TABLE'S NOTE OTHERWISE WARNS AGAINST. That
 * warning is about sorting one page of a paged register and appearing to have
 * sorted the register. This endpoint returns the whole population in a single
 * response with no paging at all, so ordering it here orders everything there is.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError } from '@/api'
import { can, users as usersApi, type PanelUser, type Role } from '@/lib/auth'
import { useAuth } from '@/components/auth-provider'
import { DataTable, PageHeader, type Column } from '@/components/data-table'
import { fmtWhen } from '@/components/state-pill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

const ROLES: Role[] = ['admin', 'power', 'viewer']

/** What each role gets, for the admin choosing one. */
const ROLE_MEANS: Record<Role, string> = {
  admin: 'Everything, including this screen.',
  power: 'Everything except managing people.',
  viewer: 'Reads everything. Changes nothing, and cannot start a call.',
}

/**
 * The three states a row can be in, in the order they are worth knowing.
 *
 * DISABLED OUTRANKS INVITED. A disabled invitee is a person who cannot get in,
 * and that is the fact somebody reading this column is looking for; "Invited"
 * would imply an email worth chasing.
 */
function status(u: PanelUser): { label: string; className: string; title: string } {
  if (u.disabled) {
    return {
      label: 'Disabled',
      className: 'border-border bg-muted text-muted-foreground',
      title: 'Cannot sign in. The account and its history are intact.',
    }
  }
  if (u.pending) {
    return {
      label: 'Invited',
      className: 'border-ev-info-border bg-ev-info-bg text-ev-info',
      title:
        u.inviteExpiresAt != null
          ? `Invited, no password set yet. The invitation expires ${fmtWhen(u.inviteExpiresAt)}.`
          : 'Invited, but has never set a password.',
    }
  }
  return {
    label: 'Active',
    className: 'border-ev-good-border bg-ev-good-bg text-ev-good',
    title: 'Has a password and can sign in.',
  }
}

/**
 * The four conflict codes this screen can provoke, as sentences with a next
 * action in them. Everything else is the server's own message, verbatim: a code
 * nobody here anticipated is better reported than paraphrased.
 */
function messageFor(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : String(e)
  switch (e.code) {
    case 'last_admin':
      return 'Refused: that would leave the panel with no administrator, and nobody could undo it. Promote somebody else to admin first.'
    case 'self_target':
      return 'Refused: an administrator cannot demote, disable or delete their own account. Ask another administrator to do it.'
    case 'email_taken':
      return 'Somebody already has an account at that address. Check the list below — they may be disabled rather than missing.'
    case 'read_only':
      return 'Your role can read this list but not change it.'
    case 'forbidden':
      return 'This action is admin-only and this account is not an admin.'
    default:
      return e.message
  }
}

export function UsersPage() {
  const { user: me } = useAuth()
  const admin = can(me, 'manageUsers')

  const [rows, setRows] = useState<PanelUser[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Bumped after every successful write. A counter rather than a boolean because
  // two consecutive writes must be two distinct dependency values.
  const [epoch, setEpoch] = useState(0)

  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  /** The one row showing "Really delete?". Single-valued, so opening a second
   *  confirmation closes the first — two armed delete buttons is not a state
   *  this screen should be able to be in. */
  const [confirming, setConfirming] = useState<string | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  // Viewer by default. The safe end of the scale is the right default on a form
  // whose mistakes are handed out by email and are awkward to take back.
  const [inviteRole, setInviteRole] = useState<Role>('viewer')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [invited, setInvited] = useState<string | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    usersApi
      .list(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setRows([...res.rows].sort((a, b) => a.email.localeCompare(b.email, 'en-GB')))
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(messageFor(e))
      })
    return () => ac.abort()
  }, [epoch])

  /**
   * One mutation, then a re-read.
   *
   * NO AbortController TIED TO THIS COMPONENT, for the reason given on Save in
   * AgentEdit.tsx: a write must not be cancelled because the reader navigated
   * away mid-flight, or the outcome becomes unknowable. A timeout bounds it.
   */
  const run = (id: string, action: (signal: AbortSignal) => Promise<unknown>) => {
    setBusyId(id)
    setActionError(null)
    setConfirming(null)
    action(AbortSignal.timeout(20_000))
      .then(() => {
        setBusyId(null)
        setEpoch((n) => n + 1)
      })
      .catch((e: unknown) => {
        setBusyId(null)
        setActionError(messageFor(e))
      })
  }

  const onInvite = (e: React.FormEvent) => {
    e.preventDefault()
    if (inviting || inviteEmail.trim() === '') return
    const email = inviteEmail.trim()
    setInviting(true)
    setInviteError(null)
    setInvited(null)
    usersApi
      .invite({ email, role: inviteRole }, AbortSignal.timeout(20_000))
      .then(() => {
        setInviting(false)
        setInviteEmail('')
        setInvited(email)
        setEpoch((n) => n + 1)
      })
      .catch((err: unknown) => {
        setInviting(false)
        setInviteError(messageFor(err))
      })
  }

  const columns: Column<PanelUser>[] = [
    {
      key: 'email',
      header: 'Email',
      cell: (u) => (
        <span className="font-medium">
          {u.email}
          {u.id === me?.id && (
            <span className="ml-1 font-normal text-muted-foreground">(you)</span>
          )}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      width: '12rem',
      cell: (u) =>
        u.name ?? (
          <span className="empty" title="Set by the person themselves when they accept their invitation.">
            not set
          </span>
        ),
    },
    {
      key: 'role',
      header: 'Role',
      width: admin ? '10rem' : '6rem',
      cell: (u) => {
        // The own row is text, not a select. See the file header: the server
        // refuses a self-demotion, so offering one is offering an error.
        if (!admin || u.id === me?.id) return <span className="mono">{u.role}</span>
        return (
          <Select
            value={u.role}
            disabled={busyId === u.id}
            onValueChange={(v) => run(u.id, (s) => usersApi.patch(u.id, { role: v as Role }, s))}
          >
            <SelectTrigger className="h-7 w-[8.5rem]" size="sm" aria-label={`Role for ${u.email}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  <span className="mono">{r}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      cell: (u) => {
        const s = status(u)
        return (
          <span className={cn('pill', s.className)} title={s.title}>
            {s.label}
          </span>
        )
      },
    },
    {
      key: 'lastLoginAt',
      header: 'Last login',
      width: '11rem',
      hideBelow: 'lg',
      cell: (u) => (
        <span className="whitespace-nowrap">
          {/* A dash here means never, which for a pending invitation is the
              expected answer rather than a missing one. */}
          {fmtWhen(u.lastLoginAt)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: '11rem',
      hideBelow: 'xl',
      cell: (u) => <span className="whitespace-nowrap">{fmtWhen(u.createdAt)}</span>,
    },
  ]

  if (admin) {
    columns.push({
      key: 'actions',
      header: '',
      width: '20rem',
      cell: (u) => {
        const self = u.id === me?.id
        const busy = busyId === u.id
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {u.pending && (
              <RowButton
                busy={busy}
                title="Issues a fresh invitation link and emails it. The previous link stops working."
                onClick={() => run(u.id, (s) => usersApi.resendInvite(u.id, s))}
              >
                Resend invite
              </RowButton>
            )}

            {/* A pending user has never signed in, so there is nothing to
                revoke. Offered on the own row deliberately — "sign me out
                everywhere" is a real thing to want after losing a laptop — with
                the consequence stated rather than discovered. */}
            {!u.pending && (
              <RowButton
                busy={busy}
                title={
                  self
                    ? 'Signs out every browser signed in as you, including this one. You will be asked to sign in again.'
                    : 'Signs them out of every browser. Their password still works.'
                }
                onClick={() => run(u.id, (s) => usersApi.revokeSessions(u.id, s))}
              >
                {self ? 'Sign out everywhere' : 'Revoke sessions'}
              </RowButton>
            )}

            {!self && (
              <RowButton
                busy={busy}
                title={
                  u.disabled
                    ? 'Lets them sign in again with their existing password.'
                    : 'Blocks sign-in and ends their sessions. Reversible, and their history is kept.'
                }
                onClick={() => run(u.id, (s) => usersApi.patch(u.id, { disabled: !u.disabled }, s))}
              >
                {u.disabled ? 'Enable' : 'Disable'}
              </RowButton>
            )}

            {!self
              && (confirming === u.id ? (
                <>
                  <RowButton
                    busy={busy}
                    variant="destructive"
                    title="Removes the account. Their name stays on the audit trail."
                    onClick={() => run(u.id, (s) => usersApi.remove(u.id, s))}
                  >
                    Really delete?
                  </RowButton>
                  <RowButton busy={false} variant="ghost" onClick={() => setConfirming(null)}>
                    Cancel
                  </RowButton>
                </>
              ) : (
                <RowButton
                  busy={busy}
                  title="Two clicks. Disabling is usually what you want instead — it is reversible."
                  onClick={() => setConfirming(u.id)}
                >
                  Delete
                </RowButton>
              ))}
          </div>
        )
      },
    })
  }

  return (
    <div className="min-w-0">
      <PageHeader
        title="Users"
        subtitle={
          <>
            Everyone who can sign in to this panel. A <span className="mono">viewer</span> may
            read every screen and change nothing; a <span className="mono">power</span> user
            may also start calls and edit agents; an <span className="mono">admin</span> may
            additionally do what is on this screen. Access to this panel is access to call
            recordings and transcripts.
          </>
        }
      />

      {admin ? (
        <section className="panel">
          <h2>
            Invite somebody
            <span className="note">they choose their own name and password</span>
          </h2>
          <form onSubmit={onInvite} className="body flex flex-wrap items-end gap-3">
            <div className="field min-w-64 flex-1 gap-1.5">
              <Label htmlFor="inviteEmail">Email</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                autoCapitalize="none"
                spellCheck={false}
                placeholder="name@example.com"
                required
              />
            </div>
            <div className="field w-48 gap-1.5">
              <Label htmlFor="inviteRole">Role</Label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as Role)}>
                <SelectTrigger id="inviteRole" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="mono">{r}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={inviting || inviteEmail.trim() === ''}>
              {inviting ? 'Sending…' : 'Send invitation'}
            </Button>
            <p className="w-full text-xs text-muted-foreground">{ROLE_MEANS[inviteRole]}</p>
            {inviteError && <p className="err w-full text-sm">{inviteError}</p>}
            {invited && (
              <p className="w-full text-sm text-ev-good">
                Invitation sent to <span className="mono">{invited}</span>. They appear below as
                Invited until they set a password.
              </p>
            )}
          </form>
        </section>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">
          Read-only: inviting people and changing roles is admin-only, and this account is a{' '}
          <span className="mono">{me?.role}</span>. Ask an admin on the list below.
        </p>
      )}

      {loadError && <p className="err mb-4">{loadError}</p>}
      {actionError && <p className="err mb-4">{actionError}</p>}

      <DataTable
        columns={columns}
        rows={rows}
        keyOf={(u) => u.id}
        emptyTitle="No users"
        emptyBody={
          <>
            The panel has no accounts at all, which should be impossible while you are reading
            this — the first admin is created by the panel worker&rsquo;s bootstrap. Treat an
            empty list here as a failed read rather than an empty table.
          </>
        }
      />
    </div>
  )
}

/**
 * One row action. Small, outlined, and never rendered to somebody who cannot use
 * it — the disabling here is only ever "a write on this row is already in
 * flight", which is a matter of timing and so is honest as a disabled state.
 */
function RowButton({
  children,
  onClick,
  busy,
  title,
  variant = 'outline',
}: {
  children: ReactNode
  onClick: () => void
  busy: boolean
  title?: string
  variant?: 'outline' | 'destructive' | 'ghost'
}) {
  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      className="h-7 px-2 text-xs"
      disabled={busy}
      title={title}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}
